import React, { useMemo } from 'react';
import {
  Card,
  Space,
  Typography,
  Button,
  Tag,
  Empty,
  Spin,
  Alert,
  Divider,
  Row,
  Col,
  Statistic,
  Dropdown,
} from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { formatDateOnly } from '../../utils/dateHelpers';
import { useGetDailyMenuCountsQuery } from '../../services/dailyMenuApi';
import { MEAL_TYPE_LABELS } from './mealTypeConstants';
import { useAppSelector } from '../../hooks/redux';
import {
  exportCSV,
  exportExcel,
  exportPDF,
  ExportConfig,
} from '../../utils/reportExport';
import type { DailyMenuCountsReport } from '../../types/dailyMenu';

const { Text, Title } = Typography;

interface Props {
  menuId: string;
  /** When true, renders inside a bordered Card; otherwise just the content (for use inside an existing card). */
  bordered?: boolean;
  /** Show the menu header (date, meal type, name). Disable when the parent already shows it. */
  showHeader?: boolean;
  /** When true, shows an Exportar dropdown (CSV/Excel/PDF) with the panel data. */
  exportable?: boolean;
}

function buildExportConfig(
  data: DailyMenuCountsReport,
  restaurantName: string,
  timestamp: string,
): ExportConfig {
  const dateLabel = formatDateOnly(data.date, 'DD/MM/YYYY');
  const mealTypeLabel = MEAL_TYPE_LABELS[data.mealType];

  return {
    reportTitle: `Menú del Día — ${data.menuName || 'Menú'}`,
    periodLabel: `${dateLabel} (${mealTypeLabel})`,
    restaurantName,
    timestamp,
    summaryCards: [
      { label: 'Total comensales', value: `${data.totalDinersOrdered}` },
      { label: 'Fecha', value: dateLabel },
      { label: 'Tipo de comida', value: mealTypeLabel },
    ],
    sections: data.courses.map((course) => ({
      title: course.isSelectable
        ? course.courseName
        : `${course.courseName} (automático)`,
      titleStyle: 'section' as const,
      headers: ['Opción', 'Cantidad'],
      rows: course.options.map((o) => [o.optionName, o.count]),
    })),
  };
}

export const DailyMenuCountsPanel: React.FC<Props> = ({
  menuId,
  bordered = true,
  showHeader = true,
  exportable = false,
}) => {
  const { data, isLoading, isFetching, isError, refetch, fulfilledTimeStamp } =
    useGetDailyMenuCountsQuery(menuId);

  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    if (!data) return;
    const config = buildExportConfig(
      data,
      currentRestaurant?.name || 'N/A',
      formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
    );
    const prefix = `menu-del-dia-${formatDateOnly(data.date, 'YYYY-MM-DD')}-${data.mealType.toLowerCase()}`;
    if (format === 'csv') exportCSV(config, prefix);
    else if (format === 'excel') exportExcel(config, prefix);
    else exportPDF(config);
  };

  const lastUpdatedLabel = useMemo(() => {
    if (!fulfilledTimeStamp) return null;
    return `Actualizado: ${formatInTimeZone(new Date(fulfilledTimeStamp), 'HH:mm:ss')}`;
  }, [fulfilledTimeStamp, formatInTimeZone]);

  const content = (
    <>
      {showHeader && data && (
        <div style={{ marginBottom: 12 }}>
          <Space align="center" wrap>
            <Title level={5} style={{ margin: 0 }}>
              {data.menuName || 'Menú del Día'}
            </Title>
            <Tag color="blue">{MEAL_TYPE_LABELS[data.mealType]}</Tag>
            <Text type="secondary">{formatDateOnly(data.date, 'DD MMM YYYY')}</Text>
          </Space>
        </div>
      )}

      <Row align="middle" justify="space-between" gutter={[8, 8]} style={{ marginBottom: 12 }}>
        <Col>
          {data && (
            <Statistic
              title="Total comensales"
              value={data.totalDinersOrdered}
              valueStyle={{ fontSize: 24 }}
            />
          )}
        </Col>
        <Col>
          <Space size="small">
            {lastUpdatedLabel && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {lastUpdatedLabel}
              </Text>
            )}
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={isFetching}
              onClick={() => refetch()}
            >
              Actualizar
            </Button>
            {exportable && (
              <Dropdown
                disabled={!data}
                menu={{
                  items: [
                    { key: 'csv', label: 'CSV', onClick: () => handleExport('csv') },
                    { key: 'excel', label: 'Excel', onClick: () => handleExport('excel') },
                    { key: 'pdf', label: 'PDF', onClick: () => handleExport('pdf') },
                  ],
                }}
              >
                <Button size="small" icon={<DownloadOutlined />}>
                  Exportar
                </Button>
              </Dropdown>
            )}
          </Space>
        </Col>
      </Row>

      {isLoading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}

      {isError && !isLoading && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el conteo"
          description="Intenta de nuevo con el botón Actualizar."
        />
      )}

      {!isLoading && !isError && data && data.courses.length === 0 && (
        <Empty description="El menú no tiene tiempos configurados" />
      )}

      {!isLoading && !isError && data && data.courses.length > 0 && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {data.courses.map((course) => (
            <div key={course.courseId}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Space size="small">
                    <Text strong>{course.courseName}</Text>
                    {!course.isSelectable && (
                      <Tag color="default" style={{ fontSize: 11 }}>
                        automático
                      </Tag>
                    )}
                  </Space>
                </Col>
                <Col>
                  <Text type="secondary">Total: {course.totalCount}</Text>
                </Col>
              </Row>
              <Divider style={{ margin: '6px 0' }} />
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {course.options.map((opt) => {
                  const isZero = opt.count === 0;
                  return (
                    <Row
                      key={`${course.courseId}-${opt.optionId ?? 'fixed'}`}
                      justify="space-between"
                      style={{ opacity: isZero ? 0.45 : 1 }}
                    >
                      <Col flex="auto">
                        <Text style={{ fontSize: 14 }}>{opt.optionName}</Text>
                      </Col>
                      <Col>
                        <Text strong style={{ fontSize: 14 }}>
                          {opt.count}
                        </Text>
                      </Col>
                    </Row>
                  );
                })}
              </Space>
            </div>
          ))}
        </Space>
      )}
    </>
  );

  if (!bordered) return content;

  return (
    <Card size="small" styles={{ body: { padding: 12 } }}>
      {content}
    </Card>
  );
};

export default DailyMenuCountsPanel;
